import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import Alerts from "../etc/list/Alerts";
// import "./UserLoginAtLocal.css";   // CSS는 index.css에 통합된 경우 불필요
// 20260903 반별 학생화면 기본 노출 개수 설정 — PBL 버전 판별용
// (이 컴포넌트 자체가 로컬서버 버전에서만 렌더링되므로 IS_LOCAL_VERSION 체크는 불필요)
import { getIsForPblModeBrowser } from "../../lib/HandsUpClient";

/* ============================================================
   쿠키 유틸리티
   ============================================================ */

/**
 * 쿠키에 JSON 값을 저장한다.
 * @param {string} name   쿠키 키
 * @param {any}    value  저장할 값 (JSON 직렬화됨)
 * @param {number} days   만료일 (기본 365일)
 */
const setCookie = (name, value, days = 365) => {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(JSON.stringify(value))}; expires=${expires}; path=/`;
};

/**
 * 쿠키에서 JSON 값을 읽어온다.
 * @param {string} name  쿠키 키
 * @returns {any|null}   파싱된 값, 없거나 파싱 실패 시 null
 */
const getCookie = (name) => {
    const match = document.cookie.match(
        new RegExp("(?:^|; )" + name.replace(/[\\^$*+?.()|[\]{}]/g, "\\$&") + "=([^;]*)")
    );
    try {
        return match ? JSON.parse(decodeURIComponent(match[1])) : null;
    } catch {
        return null;
    }
};

/* ============================================================
   기본 옵션 데이터 (props 미전달 시 fallback)
   실제 데이터는 Redux store에서
     teachers : list_ct_teacher    → { contentTeacherSeq, contentTeacherNickname }
     classes  : list_loginableLive → { liveSeq, liveName, userID, canLoginYn, ... }
   형태로 내려온다.
   ============================================================ */
const DEFAULT_TEACHERS = [
    { contentTeacherSeq: "kor", contentTeacherNickname: "국어 선생님" },
    { contentTeacherSeq: "math", contentTeacherNickname: "수학 선생님" },
    { contentTeacherSeq: "eng", contentTeacherNickname: "영어 선생님" },
    { contentTeacherSeq: "sci", contentTeacherNickname: "과학 선생님" },
    { contentTeacherSeq: "his", contentTeacherNickname: "역사 선생님" },
];

const DEFAULT_CLASSES = [
    { liveSeq: "1-1", liveName: "1학년 1반", userID: "t1", canLoginYn: "Y" },
    { liveSeq: "1-2", liveName: "1학년 2반", userID: "t2", canLoginYn: "Y" },
    { liveSeq: "1-3", liveName: "1학년 3반", userID: "t3", canLoginYn: "N" },
    { liveSeq: "2-1", liveName: "2학년 1반", userID: "t4", canLoginYn: "Y" },
    { liveSeq: "2-2", liveName: "2학년 2반", userID: "t5", canLoginYn: "N" },
];

/* ============================================================
   데이터 정규화 함수
   Redux 구조 → CustomSelect / 즐겨찾기 로직이 공통으로 사용하는
   { value, label, ...원본필드 } 형태로 변환한다.

   CustomSelect는 value/label 필드만 알면 되고,
   selectedTeacher/selectedClass 에는 원본 필드가 그대로 보존되므로
   Container에서 추가 매핑 없이 바로 사용할 수 있다.
   ============================================================ */

/**
 * list_ct_teacher 항목 정규화
 * { contentTeacherSeq, contentTeacherNickname } → { value, label, ...원본 }
 */
const normalizeTeacher = (t) => ({
    ...t,                                            // 원본 필드 전체 보존
    value: String(t.contentTeacherSeq),              // CustomSelect key / 비교용
    label: t.contentTeacherNickname,                 // 드롭다운 표시 텍스트
});

/**
 * list_loginableLive 항목 정규화
 * { liveSeq, liveName, userID, canLoginYn, ... } → { value, label, ...원본 }
 */
const normalizeClass = (c) => ({
    ...c,                                            // 원본 필드 전체 보존 (userID, canLoginYn 등)
    value: String(c.liveSeq),                        // CustomSelect key / 비교용
    label: c.liveName,                               // 드롭다운 표시 텍스트
});

/* ============================================================
   쿠키 키 상수
   ============================================================ */
const COOKIE_KEY = {
    TEACHERS: "hct_fav_teachers",
    CLASSES: "hct_fav_classes",
    AUTO_TEACHER: "hct_auto_teacher",   // 선생님 자동선택 활성 여부
    SAVED_TEACHER: "hct_saved_teacher",  // 저장된 선생님 value
    AUTO_CLASS: "hct_auto_class",     // 반 자동선택 활성 여부
    SAVED_CLASS: "hct_saved_class",    // 저장된 반 value
    // 20260903 반별 학생화면 기본 노출 개수 저장. 값 형태: { [liveSeq]: count }
    EXPOSE_COUNT_MAP: "hct_expose_count_by_class",
};

// 20260903 학생화면 기본 노출 개수 옵션 (로컬서버+PBL 버전에서만 노출/사용)
const EXPOSE_COUNT_OPTIONS = [21, 18, 15, 12, 9].map((n) => ({
    value: String(n),
    label: String(n),
}));
const DEFAULT_EXPOSE_COUNT = "12";

/* ============================================================
   Step 식별 상수
   ============================================================ */
const STEP = {
    TEACHER: "teacher",
    CLASS: "class",
};

/* ============================================================
   FavoriteModal 컴포넌트
   - 전체 항목 목록을 표시하고 ★ 토글로 즐겨찾기를 관리한다.
   - [저장] 클릭 시 쿠키에 저장 후 onClose(updatedStarred) 호출
   - [취소] 또는 오버레이 클릭 시 onClose(null) 호출 (변경 없음)
   ============================================================ */
const FavoriteModal = ({ isOpen, onClose, title, allItems, cookieKey, hint, onQuickLogin, emptyMessage }) => {
    const { t } = useTranslation();

    // 모달 내부의 임시 ★ 상태 (저장 전까지 쿠키에 반영 안 됨)
    const [starred, setStarred] = useState([]);

    // 모달이 열릴 때마다 현재 쿠키값으로 초기화
    useEffect(() => {
        if (isOpen) {
            const saved = getCookie(cookieKey);
            setStarred(Array.isArray(saved) ? saved : []);
        }
    }, [isOpen, cookieKey]);

    // ★ 토글: 이미 starred이면 제거, 아니면 추가
    const toggleStar = (value) => {
        setStarred((prev) =>
            prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
        );
    };

    // [저장] 버튼: 쿠키에 저장 후 부모에게 갱신된 목록 전달
    const handleSave = () => {
        // starred 배열에 담긴 value들을 allItems 기준으로 label(이름) 순 정렬한다.
        // allItems는 이미 정규화된 { value, label } 객체 배열이므로
        // value를 key로 label을 찾아 비교하면 된다.
        const sorted = [...starred].sort((a, b) => {
            const labelA = allItems.find((item) => item.value === a)?.label ?? "";
            const labelB = allItems.find((item) => item.value === b)?.label ?? "";
            return labelA.localeCompare(labelB, "ko"); // 한국어 이름 기준 가나다 정렬
        });

        setCookie(cookieKey, sorted);  // 정렬된 순서로 쿠키에 저장
        onClose(sorted);               // 부모에게도 정렬된 배열 전달
    };

    // [취소] 또는 오버레이 클릭: 변경 없이 닫기
    const handleCancel = () => {
        onClose(null);
    };

    // isOpen이 false이면 DOM에서 제거 (CSS transition이 필요하면 visibility로 변경 가능)
    if (!isOpen) return null;

    return (
        /* 오버레이: 클릭 시 취소 */
        <div className="ual-modal-overlay" onClick={handleCancel}>
            {/* 모달 본체: 클릭 이벤트가 오버레이로 버블링되지 않도록 중단 */}
            <div
                className="ual-modal-content"
                onClick={(e) => e.stopPropagation()}
            >
                {/* 헤더 */}
                <div className="ual-modal-header">
                    <h3>{title}</h3>
                </div>

                {/* 항목 목록: 각 행 클릭으로 ★ 토글
                    allItems(서버에서 내려온 원본 목록)이 비어있으면 안내 문구를 표시한다. */}
                <div className="ual-modal-body">
                    {allItems.length === 0 ? (
                        <div className="ual-modal-empty">
                            {emptyMessage}
                        </div>
                    ) : (
                        allItems.map((item) => (
                            <div
                                key={item.value}
                                className="ual-teacher-list-item"
                                onClick={() => toggleStar(item.value)}
                            >
                                <span>{item.label}</span>
                                <div className="ual-item-actions">
                                    {/* 20260813 접속 중인 클래스는 즐겨찾기만 허용하고 바로 로그인은 차단 */}
                                    {onQuickLogin && (
                                        <button
                                            type="button"
                                            className="ual-quick-login-btn"
                                            disabled={item.canLoginYn === "N"}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                // 현재까지 변경한 즐겨찾기 상태 먼저 저장 후 로그인
                                                handleSave();
                                                onQuickLogin(item);
                                            }}
                                        >
                                            {t("바로_로그인하기", { ns: "user" })}
                                        </button>
                                    )}
                                    <span className={`ual-star-icon ${starred.includes(item.value) ? "active" : ""}`}>
                                        ★
                                    </span>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* 푸터: 취소 / 저장 */}
                <div className="ual-modal-footer">
                    <button
                        type="button"
                        className="ual-modal-btn cancel"
                        onClick={handleCancel}
                    >
                        {t("취소", { ns: "common" })}
                    </button>
                    <button
                        type="button"
                        className="ual-modal-btn save"
                        onClick={handleSave}
                    >
                        {t("저장", { ns: "common" })}
                    </button>
                </div>

                {/* 안내 문구: hint prop이 있을 때만 표시 */}
                {hint && (
                    <p className="ual-modal-hint">
                        ℹ️ {hint}
                    </p>
                )}
            </div>
        </div>
    );
};

/* ============================================================
   CustomSelect 컴포넌트
   - isOpen / onToggle 은 부모의 openSelect state로 통합 관리
   - 목록이 비어있으면 "즐겨찾기 없음" 안내 문구 표시
   ============================================================ */
const CustomSelect = ({ placeholder, options, selectedVal, onSelect, isOpen, onToggle }) => {
    const { t } = useTranslation();

    // selectedVal은 선택된 value(string)를 받아 label을 역조회한다
    const selectedLabel = options.find((o) => o.value === selectedVal)?.label;

    return (
        <div className="ual-custom-select">
            {/* 트리거
                nativeEvent.stopImmediatePropagation() 으로 document 레벨의
                handleOutsideClick 리스너가 즉시 실행되는 것을 방지한다.
                (React 17+ 이벤트 위임 변경으로 인한 열리자마자 닫힘 버그 수정) */}
            <div
                className="ual-select-trigger"
                onClick={(e) => {
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                    onToggle();
                }}
            >
                <span style={{ color: selectedLabel ? "#f0f0f0" : "#777" }}>
                    {selectedLabel || placeholder}
                </span>
                <span className={`ual-select-arrow ${isOpen ? "open" : ""}`}>▼</span>
            </div>

            {/* 옵션 목록: 조건부 렌더링으로 DOM 자체를 제거/추가 */}
            {isOpen && (
                <div className="ual-select-options">
                    {options.length === 0 ? (
                        // 즐겨찾기가 없는 경우 안내 문구
                        <div className="ual-select-empty">
                            {t("즐겨찾기한_항목이_없습니다", { ns: "user" })}
                        </div>
                    ) : (
                        options.map((opt) => {
                            // canLoginYn이 'N'이면 비활성 항목
                            // 필드가 없는 경우(선생님 목록 등) 기본값 'Y'로 처리
                            const isDisabled = opt.canLoginYn === "N";

                            return (
                                <div
                                    key={opt.value}
                                    className={`ual-select-option ${isDisabled ? "disabled" : ""}`}
                                    onClick={() => {
                                        // 비활성 항목은 클릭해도 선택되지 않는다
                                        if (!isDisabled) onSelect(opt);
                                    }}
                                >
                                    <span>{opt.label}</span>
                                    {/* 20260813 클래스 이름 뒤에 번역된 접속 상태를 괄호로 표시 */}
                                    {isDisabled && (
                                        <span className="ual-option-badge">
                                            {" "}{t("접속중", { ns: "user" })}
                                        </span>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
};

/* ============================================================
   UserLoginAtLocal 메인 컴포넌트
   ============================================================ */
const UserLoginAtLocal = ({
    handleSelectedFunc,
    loginMessage,
    alertKind,
    savedAlwaysCloudStatus,
    teachers = DEFAULT_TEACHERS,
    classes = DEFAULT_CLASSES,
}) => {
    const { t } = useTranslation();

    /* ── Step 상태 ─────────────────────────────────────────── */
    const [currentStep, setCurrentStep] = useState(STEP.TEACHER);

    /* ── 선택값 상태 ─────────────────────────────────────────
       selectedTeacher : { value, label }        객체 또는 null
       selectedClass   : { value, label, userID } 객체 또는 null
       Label를 별도 state로 분리하지 않고 객체에서 직접 참조한다. */
    const [selectedTeacher, setSelectedTeacher] = useState(null);
    const [selectedClass, setSelectedClass] = useState(null);

    // 20260903 학생화면 기본 노출 개수 (로컬서버+PBL 버전 전용, 반별로 쿠키에 저장됨)
    const [exposeCount, setExposeCount] = useState(DEFAULT_EXPOSE_COUNT);

    /* ── 즐겨찾기 상태 ───────────────────────────────────────
       쿠키에서 읽은 즐겨찾기 value 배열.
       비어있으면 전체 목록을 드롭다운에 표시한다.              */
    const [favTeachers, setFavTeachers] = useState(() => {
        // 컴포넌트 초기 마운트 시 쿠키에서 읽어 초기값 설정
        const saved = getCookie(COOKIE_KEY.TEACHERS);
        return Array.isArray(saved) ? saved : [];
    });
    const [favClasses, setFavClasses] = useState(() => {
        const saved = getCookie(COOKIE_KEY.CLASSES);
        return Array.isArray(saved) ? saved : [];
    });

    /* ── props 정규화 ────────────────────────────────────────
       Redux에서 내려온 원본 구조를 { value, label, ...원본 } 으로
       변환한다. 이후 모든 내부 로직은 value/label 필드를 사용한다. */
    const normalizedTeachers = useMemo(() => teachers.map(normalizeTeacher), [teachers]);
    const normalizedClasses = useMemo(() => classes.map(normalizeClass), [classes]);
    // const normalizedTeachers = teachers.map(normalizeTeacher);
    // const normalizedClasses = classes.map(normalizeClass);

    // contentTeacherSeq: 1001 → value: "1001" 인 기본 선생님 고정 항목
    const DEFAULT_TEACHER_OPTION = useMemo(() => ({
        value: "1001",
        label: t("기본_선생님", { ns: "user" }),
        contentTeacherSeq: 1001,
        contentTeacherNickname: t("기본_선생님", { ns: "user" }),
    }), [t]);

    /* ── 드롭다운에 표시할 실제 옵션 목록 ───────────────────
       즐겨찾기가 있으면 즐겨찾기 항목만, 없으면 빈 배열    */
    const teacherOptions = useMemo(() => [
        DEFAULT_TEACHER_OPTION,
        ...favTeachers
            .map((favVal) => normalizedTeachers.find((t) => t.value === favVal))
            .filter(Boolean),
    ], [DEFAULT_TEACHER_OPTION, favTeachers, normalizedTeachers]);

    const classOptions = useMemo(() =>
        favClasses
            .map((favVal) => normalizedClasses.find((c) => c.value === favVal))
            .filter(Boolean),
        [favClasses, normalizedClasses]);
    /* const classOptions = favClasses
        .map((favVal) => normalizedClasses.find((c) => c.value === favVal))
        .filter(Boolean); */

    /* ── 20260903 반이 바뀔 때마다 그 반에 저장된 노출개수 값으로 동기화 ──
       저장된 값이 없거나 옵션 범위를 벗어나면 기본값(12)으로 복원한다.  */
    useEffect(() => {
        if (!selectedClass) return;
        const savedMap = getCookie(COOKIE_KEY.EXPOSE_COUNT_MAP) || {};
        const saved = savedMap[selectedClass.value];
        const savedStr = saved !== undefined && saved !== null ? String(saved) : null;
        setExposeCount(
            EXPOSE_COUNT_OPTIONS.some((opt) => opt.value === savedStr) ? savedStr : DEFAULT_EXPOSE_COUNT
        );
    }, [selectedClass]);

    /* ── 드롭다운 열림 상태 ──────────────────────────────────
       "teacher" | "class" | null – 하나만 열리도록 통합 관리  */
    const [openSelect, setOpenSelect] = useState(null);

    /* ── 모달 열림 상태 ──────────────────────────────────────
       "teacher" | "class" | null                              */
    const [openModal, setOpenModal] = useState(null);

    /* ── 체크박스 상태 ───────────────────────────────────────  */
    // const [autoLogin, setAutoLogin] = useState(false);
    const [autoTeacher, setAutoTeacher] = useState(() => getCookie(COOKIE_KEY.AUTO_TEACHER) === true);
    const [autoClass, setAutoClass] = useState(() => getCookie(COOKIE_KEY.AUTO_CLASS) === true);

    const [lAlwaysCloudStatus, setAlwaysCloudStatus] = useState(
        savedAlwaysCloudStatus === "true"
    );

    /* ── 오류 메시지 상태 ────────────────────────────────────  */
    const [errorMsg, setErrorMsg] = useState(loginMessage);
    const [errorKind, setErrorKind] = useState(alertKind);

    useEffect(() => { setErrorMsg(loginMessage); }, [loginMessage]);
    useEffect(() => { setErrorKind(alertKind); }, [alertKind]);

    /* ── Ref ─────────────────────────────────────────────────  */
    const toastRef = useRef(null);
    const timerRef = useRef(null);
    const nextBtnRef = useRef(null);
    const loginBtnRef = useRef(null);

    useEffect(() => {
        handleSelectedFunc({ selectedFunc: "GET_CONTENT_TEACHER_LIST" });
    }, []);

    /* ── 외부 클릭 시 드롭다운 닫기 ─────────────────────────  */
    useEffect(() => {
        const close = () => setOpenSelect(null);
        document.addEventListener("click", close);
        return () => document.removeEventListener("click", close);
    }, []);

    /* ── 언마운트 시 타이머 정리 ─────────────────────────────  */
    useEffect(() => {
        return () => clearTimeout(timerRef.current);
    }, []);

    /* ── 말풍선 토스트 ───────────────────────────────────────  */
    const showToast = useCallback((msg, btnRef) => {
        if (!toastRef.current || !btnRef.current) return;
        const rect = btnRef.current.getBoundingClientRect();
        toastRef.current.style.left = `${rect.left + rect.width / 2}px`;
        toastRef.current.style.top = `${window.scrollY + rect.bottom + 8}px`;
        toastRef.current.textContent = msg;
        toastRef.current.classList.add("show");
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            if (toastRef.current) toastRef.current.classList.remove("show");
        }, 2000);
    }, []);

    /* ── 선생님 자동선택
        teachers props가 로드될 때 저장된 값이 있으면 자동 선택한다.
        autoTeacher가 꺼져있으면 아무것도 하지 않는다.

        ⚠ 버그 수정: 기본 선생님(contentTeacherSeq = 1001, value = "1001")은
        서버 목록(list_ct_teacher → normalizedTeachers)에 포함되지 않는다.
        (백엔드 getContentTeacherList 쿼리가 TEACHER_SEQ = 0 을 의도적으로 제외함)
        따라서 기본 선생님으로 로그인 후 자동선택을 켜두면, 쿠키(SAVED_TEACHER)에는
        "1001"이 정상 저장되지만 다음 로그인 시 normalizedTeachers 안에서는 "1001"을
        찾지 못해 selectedTeacher가 계속 null로 남는다.
        → 로그인 화면에서 선택되지 않은 상태로 진행되며 contentTeacherSeq가
          undefined로 전달되어(media_soup 쪽에서 임의 기본값 1001로 대체됨)
          기존에 저장해둔 전송 패널/북마크 데이터가 전혀 다른 teacherSEQ 아래에서
          조회되어 "초기화된 것처럼" 보이는 원인이 된다.
        DEFAULT_TEACHER_OPTION(value="1001")도 검색 대상에 포함시켜 해결한다.       */
    useEffect(() => {
        if (!autoTeacher) return;
        const savedVal = getCookie(COOKIE_KEY.SAVED_TEACHER);
        if (!savedVal) return;
        if (savedVal === DEFAULT_TEACHER_OPTION.value) {
            setSelectedTeacher(DEFAULT_TEACHER_OPTION);
            return;
        }
        const found = normalizedTeachers.find((t) => t.value === savedVal);
        if (found) setSelectedTeacher(found);
    }, [teachers, DEFAULT_TEACHER_OPTION]); // teachers prop이 업데이트될 때(데이터 로드 시) 실행

    /* ── 반 자동선택
       classes props가 로드될 때(NEXT 클릭 후 API 응답 시) 실행된다. */
    useEffect(() => {
        if (!autoClass) return;
        const savedVal = getCookie(COOKIE_KEY.SAVED_CLASS);
        if (!savedVal) return;
        const found = normalizedClasses.find((c) => c.value === savedVal);
        // 20260813 저장된 클래스가 현재 접속 중이면 자동선택하지 않음
        setSelectedClass(found && found.canLoginYn !== "N" ? found : null);
    }, [classes]); // classes prop이 업데이트될 때(GET_LOGINABLE_LIVE_LIST 응답 시) 실행

    /* ── 즐겨찾기 모달 닫기 핸들러 ──────────────────────────
       onClose(updatedStarred) : 저장 후 호출 → state & 쿠키 갱신
       onClose(null)           : 취소 후 호출 → 변경 없음          */
    const handleModalClose = (updatedStarred) => {
        if (updatedStarred !== null) {
            if (openModal === "teacher") {
                // 260729 바로 로그인 직후에도 저장된 즐겨찾기 목록을 즉시 참조하도록 동기화
                favTeachersRef.current = updatedStarred;
                setFavTeachers(updatedStarred);

                // 현재 선택된 선생님이 즐겨찾기에서 빠졌으면 선택 초기화
                // ✅ 수정: updatedStarred.length > 0 조건 제거
                //    → 즐겨찾기를 전부 해제해서 빈 배열이 된 경우에도
                //      selectedTeacher를 null로 초기화해야 함
                if (
                    selectedTeacher &&
                    !updatedStarred.includes(selectedTeacher.value)
                ) {
                    setSelectedTeacher(null);

                    // ✅ 추가: 해당 선생님이 즐겨찾기에서 제거됐으므로
                    //    자동선택 체크박스 상태 및 저장된 쿠키도 함께 초기화
                    //    (재접속 시 이전 값으로 자동선택되는 버그 방지)
                    setAutoTeacher(false);
                    setCookie(COOKIE_KEY.AUTO_TEACHER, false);
                    setCookie(COOKIE_KEY.SAVED_TEACHER, null);
                }
            } else if (openModal === "class") {
                // 260729 바로 로그인 직후에도 저장된 즐겨찾기 목록을 즉시 참조하도록 동기화
                favClassesRef.current = updatedStarred;
                setFavClasses(updatedStarred);

                // ✅ 수정: updatedStarred.length > 0 조건 제거 (동일 이유)
                if (
                    selectedClass &&
                    !updatedStarred.includes(selectedClass.value)
                ) {
                    setSelectedClass(null);

                    // ✅ 추가: 반 자동선택 체크박스 상태 및 저장된 쿠키도 함께 초기화
                    setAutoClass(false);
                    setCookie(COOKIE_KEY.AUTO_CLASS, false);
                    setCookie(COOKIE_KEY.SAVED_CLASS, null);
                }
            }
        }
        setOpenModal(null);
    };

    /* ── 선생님 선택 ─────────────────────────────────────────
       CustomSelect에서 opt 객체 { value, label } 전체를 받아 저장 */
    const handleSelectTeacher = (opt) => {
        setSelectedTeacher(opt);
        setOpenSelect(null);
        handleSelectedFunc({ selectedFunc: "L_MESSAGE", message: "", kind: "none" });
    };

    /* ── 반 선택 ─────────────────────────────────────────────
       CustomSelect에서 opt 객체 { value, label, userID } 전체를 받아 저장 */
    const handleSelectClass = (opt) => {
        setSelectedClass(opt);
        setOpenSelect(null);
        handleSelectedFunc({ selectedFunc: "L_MESSAGE", message: "", kind: "none" });
    };

    /* ── 20260903 학생화면 기본 노출 개수 선택 ─────────────────── */
    const handleSelectExposeCount = (opt) => {
        setExposeCount(opt.value);
        setOpenSelect(null);
    };

    /* ── NEXT ────────────────────────────────────────────────  */
    const handleNext = () => {
        if (!selectedTeacher) {
            showToast(t("로그인할_선생님을_선택해_주세요", { ns: "user" }), nextBtnRef);
            return;
        }
        setCurrentStep(STEP.CLASS);
        handleSelectedFunc({ selectedFunc: "GET_LOGINABLE_LIVE_LIST" });
    };

    /* ── 뒤로가기 ────────────────────────────────────────────  */
    // classes useEffect를 재트리거할 수 없으므로, handleBack에서 직접 복구 예약
    const handleBack = () => {
        /* setSelectedClass(null);
        setOpenSelect(null);
        setCurrentStep(STEP.TEACHER); */

        // setSelectedClass(null);
        setOpenSelect(null);
        setCurrentStep(STEP.TEACHER);

        // ▼ 핵심 수정: classes prop 변경 여부와 무관하게 자동선택값을 복원 예약
        // NEXT 클릭 → setCurrentStep(STEP.CLASS) → CLASS step 렌더링 직전에
        // selectedClass가 복구되어 있어야 하므로 여기서 setState를 호출한다.
        // (React 배치 업데이트로 인해 이 setState는 다음 렌더에 반영됨)
        if (autoClassRef.current) {
            const savedVal = getCookie(COOKIE_KEY.SAVED_CLASS);
            if (savedVal) {
                // normalizedClasses는 classes prop 기준으로 만들어진 최신값을 써야 함.
                // 이 시점에서 classes prop은 변경되지 않았으므로 현재 normalizedClasses 사용.
                const found = normalizedClasses.find((c) => c.value === savedVal);
                // 20260813 저장된 클래스가 현재 접속 중이면 복원하지 않음
                setSelectedClass(found && found.canLoginYn !== "N" ? found : null);
            }
        }
    };

    /* ── LOGIN ───────────────────────────────────────────────────────
       로그인 요청을 dispatch하고, loginAttemptedRef를 true로 세팅한다.
       실제 쿠키 저장은 언마운트 클린업에서 처리.
       ──────────────────────────────────────────────────────────────── */
    const handleLogin = () => {
        if (!selectedClass) {
            showToast(t("로그인할_반을_선택해_주세요", { ns: "user" }), loginBtnRef);
            return;
        }
        const loginSelectedTeacher = selectedTeacher || DEFAULT_TEACHER_OPTION;

        // 로그인 버튼을 눌렀음을 표시 → 언마운트 클린업에서 쿠키 저장 허용
        loginAttemptedRef.current = true;

        // 20260903 반별 학생화면 기본 노출 개수 저장. autoClass(자동로그인) 체크 여부와
        // 무관하게 항상 저장한다 — 자동로그인 편의 설정과는 별개의 화면 취향이기 때문.
        if (getIsForPblModeBrowser()) {
            const savedMap = getCookie(COOKIE_KEY.EXPOSE_COUNT_MAP) || {};
            setCookie(COOKIE_KEY.EXPOSE_COUNT_MAP, {
                ...savedMap,
                [selectedClass.value]: Number(exposeCount),
            });
        }

        handleSelectedFunc({ selectedFunc: "L_MESSAGE", message: "", kind: "none" });
        handleSelectedFunc({
            selectedFunc: "LOGIN_WITHOUT_PW",
            loginInfo: {
                userID: selectedClass.userID,  // 반 객체 안의 userID 사용
                selectedTeacher: loginSelectedTeacher,               // { value, label } 객체
                selectedClass,                 // { value, label, userID } 객체
                isAutoLogin: autoTeacher,
            },
            mode: "hiclass",
        });
    };

    /* ── 선생님 목록 모달에서 바로 로그인하기 ──────────────────
       클릭한 선생님을 selectedTeacher로 설정하고 Step 2로 이동  */
    const handleQuickLoginTeacher = (item) => {
        setSelectedTeacher(item);
        setOpenModal(null);
        setCurrentStep(STEP.CLASS);
        handleSelectedFunc({ selectedFunc: "GET_LOGINABLE_LIVE_LIST" });
    };

    const handleQuickLoginClass = (item) => {
        setSelectedClass(item);
        setOpenModal(null);
        const loginSelectedTeacher = selectedTeacher || DEFAULT_TEACHER_OPTION;

        // 로그인 버튼을 눌렀음을 표시 → 언마운트 클린업에서 쿠키 저장 허용
        loginAttemptedRef.current = true;

        handleSelectedFunc({ selectedFunc: "L_MESSAGE", message: "", kind: "none" });
        handleSelectedFunc({
            selectedFunc: "LOGIN_WITHOUT_PW",
            loginInfo: {
                // userID: selectedClass.userID,  // 반 객체 안의 userID 사용
                userID: item.userID,
                selectedTeacher: loginSelectedTeacher,               // { value, label } 객체
                selectedClass: item,                 // { value, label, userID } 객체
                isAutoLogin: autoTeacher,
            },
            mode: "hiclass",
        });
    }

    /* ── 로그인 성공 여부 Ref ────────────────────────────────────────
       로그인 버튼을 눌렀는지 여부를 ref로 추적한다.
       
       isLoginYn이 "Y"가 되는 순간 이 컴포넌트가 언마운트되므로
       useEffect([isLoginYn])으로는 "Y" 변경을 캐치할 수 없다.
       (렌더 전에 컴포넌트가 사라짐)

       대신 언마운트 클린업(return () => {})에서 쿠키를 저장하되,
       "로그인 버튼을 눌러서 언마운트된 것"과
       "C# 프로그램 종료로 WebView가 닫혀서 언마운트된 것"을 구분하기 위해
       loginAttemptedRef 플래그를 사용한다.

       흐름:
         handleLogin() 호출 → loginAttemptedRef.current = true 세팅
         → 로그인 성공 → 컴포넌트 언마운트
         → 클린업 실행: loginAttemptedRef.current === true 이므로 쿠키 저장 ✅

         C# 종료 → 컴포넌트 언마운트
         → 클린업 실행: loginAttemptedRef.current === false 이므로 쿠키 저장 안함 ✅
         
         로그인 실패 → 컴포넌트 언마운트되지 않음
         → 클린업 실행되지 않음 → 쿠키 저장 안함 ✅
       ──────────────────────────────────────────────────────────────── */
    const loginAttemptedRef = useRef(false);

    /* ── 언마운트 시 쿠키 저장 ───────────────────────────────────────
       의존성 배열을 []로 설정하여 마운트 시 한 번만 등록.
       클린업 함수 안에서 ref와 state의 최신값을 클로저로 읽는다.

       ※ autoTeacher, autoClass, selectedTeacher, selectedClass를
         의존성 배열에 넣으면 값이 바뀔 때마다 클린업+재등록이 반복되므로
         의도치 않은 중간 시점에 저장될 수 있다. ref로 최신값을 유지하고
         클린업에서 ref를 읽는 패턴이 더 안전하다.
       ──────────────────────────────────────────────────────────────── */
    const autoTeacherRef = useRef(autoTeacher);
    const autoClassRef = useRef(autoClass);
    const selectedTeacherRef = useRef(selectedTeacher);
    const selectedClassRef = useRef(selectedClass);
    // 260729 즐겨찾기로 등록된 선택값만 자동 로그인 쿠키에 저장하기 위한 최신 목록
    const favTeachersRef = useRef(favTeachers);
    const favClassesRef = useRef(favClasses);

    // state가 바뀔 때마다 ref를 최신값으로 동기화
    useEffect(() => { autoTeacherRef.current = autoTeacher; }, [autoTeacher]);
    useEffect(() => { autoClassRef.current = autoClass; }, [autoClass]);
    useEffect(() => { selectedTeacherRef.current = selectedTeacher; }, [selectedTeacher]);
    useEffect(() => { selectedClassRef.current = selectedClass; }, [selectedClass]);
    useEffect(() => { favTeachersRef.current = favTeachers; }, [favTeachers]);
    useEffect(() => { favClassesRef.current = favClasses; }, [favClasses]);

    useEffect(() => {
        // 클린업: 컴포넌트가 언마운트될 때 실행
        return () => {
            // loginAttemptedRef가 false이면 C# 종료 등 외부 요인으로 닫힌 것
            // → 쿠키 저장하지 않음
            if (!loginAttemptedRef.current) return;

            // loginAttemptedRef가 true이면 로그인 버튼을 눌러서 언마운트된 것
            // (로그인 실패 시에는 컴포넌트가 언마운트되지 않으므로 여기까지 오지 않음)
            // 260729 전체 목록의 바로 로그인 항목은 즐겨찾기일 때만 선택값 쿠키에 저장
            // 20260813 기본 선생님은 즐겨찾기 목록과 무관하게 자동선택 값으로 저장
            if (
                autoTeacherRef.current &&
                selectedTeacherRef.current &&
                (
                    selectedTeacherRef.current.value === "1001" ||
                    favTeachersRef.current.includes(selectedTeacherRef.current.value)
                )
            ) {
                setCookie(COOKIE_KEY.SAVED_TEACHER, selectedTeacherRef.current.value);
            }
            if (
                autoClassRef.current &&
                selectedClassRef.current &&
                favClassesRef.current.includes(selectedClassRef.current.value)
            ) {
                setCookie(COOKIE_KEY.SAVED_CLASS, selectedClassRef.current.value);
            }
        };
    }, []); // 마운트 시 한 번만 클린업 등록

    /* ── 선생님 자동선택 체크박스 ─────────────────────────────────────
       [수정 전] 체크박스를 바꾸는 시점에 selectedTeacher 값을 쿠키에 저장
                 → 즐겨찾기 목록에서 선생님을 먼저 선택하지 않은 상태에서
                   체크박스를 켜면, 이 시점에 selectedTeacher가 null이라
                   SAVED_TEACHER가 null로 저장되는 버그 발생.
                   이후 즐겨찾기 클릭으로 선생님을 선택해도 체크박스 이벤트가
                   다시 발생하지 않으므로 쿠키가 갱신되지 않음.

       [수정 후] 체크박스는 AUTO_TEACHER 플래그(on/off)만 쿠키에 기록.
                 실제 SAVED_TEACHER 값은 로그인 버튼 성공 시점(handleLogin)에
                 한꺼번에 저장하므로 선택 순서와 무관하게 항상 올바르게 저장됨.
                 단, 체크를 해제할 때는 저장된 선택값을 즉시 삭제하여
                 다음 자동로그인 시 이전 값이 사용되지 않도록 한다.
       ──────────────────────────────────────────────────────────────── */
    const onChangeAutoTeacher = (e) => {
        const checked = e.target.checked;
        setAutoTeacher(checked);

        // ✅ AUTO_TEACHER 플래그(활성 여부)만 쿠키에 저장
        setCookie(COOKIE_KEY.AUTO_TEACHER, checked);

        // 체크 해제 시: 저장된 선생님 값을 즉시 삭제
        // (자동선택이 꺼진 상태에서 이전 값이 남아 있으면 오동작 가능성 있음)
        if (!checked) {
            setCookie(COOKIE_KEY.SAVED_TEACHER, null);
        }
        // ❌ checked && selectedTeacher 시 여기서 저장하지 않음
        //    → 이 시점엔 selectedTeacher가 null일 수 있으므로
        //      실제 저장은 handleLogin()에서 처리
    };

    /* ── 반 자동선택 체크박스 ─────────────────────────────────────────
       [수정 전] 체크박스를 바꾸는 시점에 selectedClass 값을 쿠키에 저장
                 → onChangeAutoTeacher와 동일한 순서 의존 버그 존재.

       [수정 후] AUTO_CLASS 플래그만 쿠키에 기록.
                 SAVED_CLASS 값은 handleLogin() 성공 시점에 저장.
                 체크 해제 시에만 저장된 값을 즉시 삭제.
       ──────────────────────────────────────────────────────────────── */
    const onChangeAutoClass = (e) => {
        const checked = e.target.checked;
        setAutoClass(checked);

        // ✅ AUTO_CLASS 플래그(활성 여부)만 쿠키에 저장
        setCookie(COOKIE_KEY.AUTO_CLASS, checked);

        // 체크 해제 시: 저장된 반 값을 즉시 삭제
        if (!checked) {
            setCookie(COOKIE_KEY.SAVED_CLASS, null);
        }
        // ❌ 실제 선택값 저장은 handleLogin()에서 처리
    };

    /* ── 드롭다운 토글 ───────────────────────────────────────  */
    const toggleTeacherSelect = useCallback(() => {
        setOpenSelect((prev) => (prev === "teacher" ? null : "teacher"));
    }, []);

    const toggleClassSelect = useCallback(() => {
        setOpenSelect((prev) => (prev === "class" ? null : "class"));
    }, []);

    // 20260903
    const toggleExposeCountSelect = useCallback(() => {
        setOpenSelect((prev) => (prev === "exposeCount" ? null : "exposeCount"));
    }, []);

    /* ── 클라우드 / ID·PW 찾기 ───────────────────────────────  */
    const handleGoToCloud = () => handleSelectedFunc({ selectedFunc: "GO_TO_CLOUD", kind: lAlwaysCloudStatus });
    const handleFindToggle = () => handleSelectedFunc({ selectedFunc: "FIND_TOGGLE", mode: "web" });

    /* ── 설정 버튼 아이콘 (SVG) ──────────────────────────────  */
    const ListIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
            viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            aria-hidden="true">
            <line x1="8" y1="6" x2="21" y2="6" />
            <line x1="8" y1="12" x2="21" y2="12" />
            <line x1="8" y1="18" x2="21" y2="18" />
            <line x1="3" y1="6" x2="3.01" y2="6" />
            <line x1="3" y1="12" x2="3.01" y2="12" />
            <line x1="3" y1="18" x2="3.01" y2="18" />
        </svg>
    );

    /* ============================================================
       렌더링
       ============================================================ */
    return (
        <div className="ual-wrapper">
            <div className="ual-card">

                {/* ── 고정형 상단 헤더 ──────────────────────────────
                    [뒤로가기] [배지] [설정버튼]  3-열 구조.
                    설정 버튼은 현재 Step에 맞는 즐겨찾기 모달을 열어준다. */}
                <div className="ual-fixed-header">

                    {/* 뒤로가기: Step CLASS 에서만 표시 */}
                    <div className="ual-back-space">
                        <button
                            type="button"
                            className={`ual-back-btn ${currentStep === STEP.CLASS ? "visible" : ""}`}
                            onClick={handleBack}
                            aria-label={t("이전_단계로", { ns: "user" })}
                        >
                            ←
                        </button>
                    </div>

                    {/* 배지 */}
                    <div className="ual-badge">
                        <svg
                            className={`ual-badge-svg ${selectedTeacher ? "selected" : ""}`}
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                        >
                            <path d="M17.925 20.056a6 6 0 0 0-11.851.001" />
                            <circle cx="12" cy="11" r="4" />
                            <circle cx="12" cy="12" r="10" />
                        </svg>
                        <span className={`ual-badge-text ${selectedTeacher ? "selected" : ""}`}>
                            {selectedTeacher?.label || t("선생님을_선택해_주세요", { ns: "user" })}
                        </span>
                    </div>
                    <div className="ual-back-space" />
                </div>

                {/* ── Step 1: 선생님 선택 ─────────────────────────── */}
                <div className={`ual-step ${currentStep === STEP.TEACHER ? "active" : ""}`}>
                    <button type="button" className="ual-list-settings-btn"
                        onClick={() => setOpenModal(STEP.TEACHER)}>
                        <ListIcon />
                        <span className="ual-settings-btn-text">
                            {t("전체_선생님_목록", { ns: "user" })}
                        </span>
                    </button>
                    <div className="ual-input-group">
                        <CustomSelect
                            placeholder={t("로그인할_선생님을_선택해_주세요", { ns: "user" })}
                            options={teacherOptions}
                            selectedVal={selectedTeacher?.value}   // 객체에서 value만 추출
                            onSelect={handleSelectTeacher}
                            isOpen={openSelect === "teacher"}
                            onToggle={toggleTeacherSelect}
                        />
                    </div>
                    <div className="ual-alert-wrap">
                        <Alerts kind={errorKind} message={errorMsg} style={{ width: "100%", marginBottom: "1rem" }} />
                    </div>
                    <button type="button" ref={nextBtnRef} className="ual-btn" onClick={handleNext}>
                        {t("NEXT", { ns: "user" }) || "NEXT"}
                    </button>
                </div>

                {/* ── Step 2: 반 선택 ─────────────────────────────── */}
                <div className={`ual-step ${currentStep === STEP.CLASS ? "active" : ""}`}>
                    <button type="button" className="ual-list-settings-btn"
                        onClick={() => setOpenModal(STEP.CLASS)}>
                        <ListIcon />
                        <span className="ual-settings-btn-text">
                            {t("전체_학급_목록", { ns: "user" })}
                        </span>
                    </button>
                    <div className="ual-input-group">
                        <CustomSelect
                            placeholder={t("로그인할_반을_선택해_주세요", { ns: "user" })}
                            options={classOptions}
                            selectedVal={selectedClass?.value}     // 객체에서 value만 추출
                            onSelect={handleSelectClass}
                            isOpen={openSelect === "class"}
                            onToggle={toggleClassSelect}
                        />
                    </div>

                    {/* 20260903 학생화면 기본 노출 개수 (로컬서버+PBL 버전에서만 노출) */}
                    {getIsForPblModeBrowser() && (
                        <div className="ual-input-group">
                            <CustomSelect
                                placeholder={t("학생화면_기본_노출_개수", { ns: "user" })}
                                options={EXPOSE_COUNT_OPTIONS}
                                selectedVal={exposeCount}
                                onSelect={handleSelectExposeCount}
                                isOpen={openSelect === "exposeCount"}
                                onToggle={toggleExposeCountSelect}
                            />
                        </div>
                    )}

                    <div className="ual-alert-wrap">
                        <Alerts kind={errorKind} message={errorMsg} style={{ width: "100%", marginBottom: "1rem" }} />
                    </div>
                    <button type="button" ref={loginBtnRef} className="ual-btn" onClick={handleLogin}>
                        {t("LOGIN", { ns: "user" }) || "LOGIN"}
                    </button>
                </div>

                {/* <label className="ual-check-label">
                    <input type="checkbox" className="ual-check-input" id="rememberCheck" checked={autoLogin} onChange={(e) => setAutoLogin(e.target.checked)} />
                    <span className="ual-check-box" />
                    <span className="ual-check-text">
                        <span className="ual-blue">{t("항상", { ns: "user" }) || "항상"}</span>{" "}이 정보로 로그인
                    </span>
                </label> */}

                {/* 선생님 자동선택: Step 1(TEACHER)에서만 표시 */}
                {currentStep === STEP.TEACHER && (
                    <label className="ual-check-label">
                        <input type="checkbox" className="ual-check-input" id="rememberCheck"
                            checked={autoTeacher}
                            onChange={onChangeAutoTeacher}
                        />
                        <span className="ual-check-box" />
                        <span className="ual-check-text">
                            <span className="ual-blue">{t("항상", { ns: "user" }) || "항상"}</span>
                            {" "}
                            <span>{t("이_선생님으로_로그인", { ns: "user" })}</span>
                        </span>
                    </label>
                )}

                {/* 반 자동선택: Step 2(CLASS)에서만 표시 */}
                {currentStep === STEP.CLASS && (
                    <label className="ual-check-label">
                        <input type="checkbox" className="ual-check-input" id="rememberClassCheck"
                            checked={autoClass}
                            onChange={onChangeAutoClass}
                        />
                        <span className="ual-check-box" />
                        <span className="ual-check-text">
                            <span className="ual-blue">{t("항상", { ns: "user" }) || "항상"}</span>
                            {" "}
                            <span>{t("이_반으로_로그인", { ns: "user" })}</span>
                        </span>
                    </label>
                )}

                {/* ── 공통 체크박스 ─────────────────────────────────  */}
                <label className="ual-check-label">
                    <input type="checkbox" className="ual-check-input" id="cloudCheck" checked={lAlwaysCloudStatus} onChange={(e) => setAlwaysCloudStatus(e.target.checked)} />
                    <span className="ual-check-box" />
                    <span className="ual-check-text">
                        <span className="ual-blue">{t("항상", { ns: "user" }) || "항상"}</span>
                        {" "}
                        <span>{t("클라우드로_이동", { ns: "user" }) || "클라우드로 이동"}</span>
                        {/* <span className="ual-cloud-link" onClick={(e) => { e.preventDefault(); handleGoToCloud(); }}>
                            {t("클라우드로_이동", { ns: "user" }) || "클라우드로 이동"}
                        </span> */}
                    </span>
                </label>

                {/* ── ID/PW 찾기 ────────────────────────────────────  */}
                {/* <div className="ual-footer-links">
                    <span className="ual-forgot-text">
                        {t("Forgot", { ns: "user" }) || "Forgot"}
                        <span className="ual-forgot-link" onClick={handleFindToggle}>
                            {" "}<u>{t("ID/Password", { ns: "user" }) || "ID/Password"}</u>
                        </span>
                    </span>
                </div> */}

                {/* ── 클라우드로 이동 버튼 ───────────────────────────
                    기존 ual-cloud-link의 handleGoToCloud() 역할을 버튼으로 분리  */}
                <div className="ual-action-btns-wrapper">
                    <button
                        type="button"
                        className="ual-cloud-btn"
                        onClick={(e) => { e.preventDefault(); handleGoToCloud(); }}
                    >
                        {/* 클라우드 아이콘 SVG */}
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                        >
                            <polyline points="16 16 12 12 8 16" />
                            <line x1="12" y1="12" x2="12" y2="21" />
                            <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                        </svg>
                        {t("클라우드로_이동", { ns: "user" }) || "클라우드로 이동"}
                    </button>
                </div>
            </div>

            {/* ── 즐겨찾기 모달 ──────────────────────────────────────
                openModal 이 "teacher"/"class" 일 때 표시.
                allItems: 전체 목록 (즐겨찾기 선택 모달은 항상 전체를 보여줌)
                cookieKey: 저장할 쿠키 키                               */}
            <FavoriteModal
                isOpen={openModal === "teacher"}
                onClose={handleModalClose}
                title={t("선생님_즐겨찾기_관리", { ns: "user" })}
                allItems={normalizedTeachers}
                cookieKey={COOKIE_KEY.TEACHERS}
                hint={t("선생님_목록_관리_안내", { ns: "user" })}
                onQuickLogin={handleQuickLoginTeacher}
                emptyMessage={t("사용_가능한_선생님이_없습니다", { ns: "user" })}
            />
            <FavoriteModal
                isOpen={openModal === "class"}
                onClose={handleModalClose}
                title={t("반_즐겨찾기_관리", { ns: "user" })}
                allItems={normalizedClasses}
                cookieKey={COOKIE_KEY.CLASSES}
                hint={t("클래스_목록_관리_안내", { ns: "user" })}
                onQuickLogin={handleQuickLoginClass}
                emptyMessage={t("사용_가능한_반이_없습니다", { ns: "user" })}
            />

            {/* 말풍선 Toast */}
            <div ref={toastRef} className="ual-toast" role="alert" aria-live="polite" />
        </div>
    );
};

export default UserLoginAtLocal;
